import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Import hero images for each product
import heroGrass from '@/assets/new-hero-grass-depot.jpg';
import heroRock from '@/assets/decorative-rock-types.jpg';
import heroMulch from '@/assets/mulch-varieties-display.jpg';
import heroSoil from '@/assets/premium-soil-varieties.jpg';
import heroDelivery from '@/assets/bulk-delivery-hero.jpg';

interface HeroSlide {
  id: string;
  image: string;
  titleEn: string;
  titleEs: string;
  subtitleEn: string;
  subtitleEs: string;
  ctaLink: string;
  badgeEn: string;
  badgeEs: string;
}

const heroSlides: HeroSlide[] = [
  {
    id: 'grass',
    image: heroGrass,
    titleEn: 'Premium Texas Grass',
    titleEs: 'Césped Premium de Texas',
    subtitleEn: 'Bermuda, St. Augustine, and Zoysia varieties perfect for Texas heat. Transform your lawn today!',
    subtitleEs: '¡Variedades de Bermuda, San Agustín y Zoysia perfectas para el calor de Texas. Transforma tu jardín hoy!',
    ctaLink: '/grass',
    badgeEn: '🌱 Best Seller',
    badgeEs: '🌱 Más Vendido',
  },
  {
    id: 'rock',
    image: heroRock,
    titleEn: 'Decorative Rock & Gravel',
    titleEs: 'Roca Decorativa y Grava',
    subtitleEn: 'River rock, limestone, decomposed granite and more. Low-maintenance landscaping that lasts.',
    subtitleEs: 'Roca de río, piedra caliza, granito descompuesto y más. Paisajismo de bajo mantenimiento que perdura.',
    ctaLink: '/rock',
    badgeEn: '🪨 Low Maintenance',
    badgeEs: '🪨 Bajo Mantenimiento',
  },
  {
    id: 'mulch',
    image: heroMulch,
    titleEn: 'Premium Mulch Selection',
    titleEs: 'Selección Premium de Mulch',
    subtitleEn: 'Hardwood, cedar, pine bark and colored options. Protect your plants and beautify your beds.',
    subtitleEs: 'Opciones de madera dura, cedro, corteza de pino y colores. Protege tus plantas y embellece tus jardines.',
    ctaLink: '/mulch',
    badgeEn: '🌿 Garden Essential',
    badgeEs: '🌿 Esencial del Jardín',
  },
  {
    id: 'soil',
    image: heroSoil,
    titleEn: 'Rich Topsoil & Compost',
    titleEs: 'Tierra Fértil y Composta',
    subtitleEn: 'Premium soil blends for gardens, lawns, and landscaping projects. Feed your plants right.',
    subtitleEs: 'Mezclas de tierra premium para jardines, césped y proyectos de paisajismo. Nutre tus plantas correctamente.',
    ctaLink: '/soil',
    badgeEn: '🌻 Nutrient Rich',
    badgeEs: '🌻 Rico en Nutrientes',
  },
  {
    id: 'delivery',
    image: heroDelivery,
    titleEn: 'Free Local Delivery',
    titleEs: 'Entrega Local Gratis',
    subtitleEn: 'Bulk materials delivered to your door in Montgomery County. Save time and hassle!',
    subtitleEs: '¡Materiales a granel entregados a tu puerta en el Condado de Montgomery. Ahorra tiempo y esfuerzo!',
    ctaLink: '/bulk-delivery',
    badgeEn: '🚚 Fast & Free',
    badgeEs: '🚚 Rápido y Gratis',
  },
];

const HeroSection = () => {
  const { language, t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToSlide = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning]);

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % heroSlides.length);
  }, [currentSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide((currentSlide - 1 + heroSlides.length) % heroSlides.length);
  }, [currentSlide, goToSlide]);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      nextSlide();
    }, 6000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  // Pause auto-play on user interaction
  const handleUserInteraction = () => {
    setIsAutoPlaying(false);
    // Resume after 15 seconds of no interaction
    setTimeout(() => setIsAutoPlaying(true), 15000);
  };

  const slide = heroSlides[currentSlide];
  const title = language === 'es' ? slide.titleEs : slide.titleEn;
  const subtitle = language === 'es' ? slide.subtitleEs : slide.subtitleEn;
  const badge = language === 'es' ? slide.badgeEs : slide.badgeEn;

  return (
    <section className="relative h-[75vh] min-h-[550px] flex items-center justify-center overflow-hidden">
      {/* Background Images with Crossfade */}
      {heroSlides.map((s, index) => (
        <img 
          key={s.id}
          src={s.image}
          alt={`The Grass Depot - ${s.titleEn}`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
          width="1335"
          height="658"
          fetchPriority={index === 0 ? "high" : "low"}
          loading={index === 0 ? "eager" : "lazy"}
        />
      ))}
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
      
      {/* Content */}
      <div className="relative container mx-auto px-4 text-center lg:text-left z-10">
        <div className="max-w-3xl">
          {/* Product Badge */}
          <div 
            className={`inline-block mb-4 px-4 py-2 bg-accent/90 text-accent-foreground rounded-full text-sm font-semibold backdrop-blur-sm transition-all duration-500 ${
              isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
            }`}
          >
            {badge}
          </div>
          
          {/* Title with Animation */}
          <h1 
            className={`text-4xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 leading-tight transition-all duration-500 ${
              isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
            }`}
          >
            {title}
          </h1>
          
          {/* Subtitle with Animation */}
          <p 
            className={`text-lg lg:text-xl xl:text-2xl text-white/90 mb-8 leading-relaxed max-w-2xl transition-all duration-500 delay-100 ${
              isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
            }`}
          >
            {subtitle}
          </p>
          
          {/* CTA Buttons */}
          <div 
            className={`flex flex-col sm:flex-row gap-4 justify-center lg:justify-start transition-all duration-500 delay-200 ${
              isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
            }`}
          >
            <Link to={`/${language}${slide.ctaLink}`}>
              <Button variant="hero" size="lg" className="w-full sm:w-auto text-lg px-8 py-4 shadow-lg hover:shadow-xl transition-shadow">
                {language === 'es' ? 'Ver Productos' : 'View Products'}
              </Button>
            </Link>
            
            <Link to={`/${language}/quote`}>
              <Button variant="outline" size="lg" className="w-full sm:w-auto text-lg px-8 py-4 bg-white/95 border-white text-primary hover:bg-white hover:text-primary backdrop-blur-sm">
                {t('hero.cta.quote')}
              </Button>
            </Link>
            
            <a href="tel:9363200763" className="hidden md:block">
              <Button variant="ghost" size="lg" className="text-lg px-6 py-4 text-white hover:bg-white/20">
                {t('hero.cta.call')}
              </Button>
            </a>
          </div>
        </div>
      </div>
      
      {/* Slide Navigation Arrows */}
      <button 
        onClick={() => { prevSlide(); handleUserInteraction(); }}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-all hidden md:flex items-center justify-center"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      
      <button 
        onClick={() => { nextSlide(); handleUserInteraction(); }}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-all hidden md:flex items-center justify-center"
        aria-label="Next slide"
      >
        <ChevronRight className="w-6 h-6" />
      </button>
      
      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
        {heroSlides.map((s, index) => (
          <button
            key={s.id}
            onClick={() => { goToSlide(index); handleUserInteraction(); }}
            className={`group relative transition-all duration-300 ${
              index === currentSlide ? 'w-10' : 'w-3'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          >
            <div 
              className={`h-3 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'bg-accent w-full' 
                  : 'bg-white/40 hover:bg-white/60 w-3'
              }`}
            />
            {/* Progress bar for current slide */}
            {index === currentSlide && isAutoPlaying && (
              <div 
                className="absolute inset-0 h-3 rounded-full bg-white/30 origin-left animate-progress"
                style={{ animationDuration: '6s' }}
              />
            )}
          </button>
        ))}
      </div>
      
      {/* Trust Indicators - Mobile optimized */}
      <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-10 flex flex-wrap gap-4 justify-center text-white/80 max-w-xl px-4 md:hidden">
        <div className="flex items-center space-x-2 bg-black/30 backdrop-blur-sm rounded-full px-3 py-1">
          <div className="w-2 h-2 bg-accent rounded-full"></div>
          <span className="text-xs">{language === 'es' ? 'Entrega Rápida' : 'Fast Delivery'}</span>
        </div>
        <div className="flex items-center space-x-2 bg-black/30 backdrop-blur-sm rounded-full px-3 py-1">
          <div className="w-2 h-2 bg-accent rounded-full"></div>
          <span className="text-xs">{language === 'es' ? 'Calidad Premium' : 'Premium Quality'}</span>
        </div>
      </div>
      
      {/* Desktop Trust Indicators */}
      <div className="hidden md:flex absolute bottom-8 right-8 z-10 flex-col gap-2 text-white/80">
        <div className="flex items-center space-x-2 bg-black/30 backdrop-blur-sm rounded-lg px-4 py-2">
          <div className="w-3 h-3 bg-accent rounded-full"></div>
          <span className="text-sm">{language === 'es' ? 'Condado de Montgomery' : 'Montgomery County'}</span>
        </div>
        <div className="flex items-center space-x-2 bg-black/30 backdrop-blur-sm rounded-lg px-4 py-2">
          <div className="w-3 h-3 bg-accent rounded-full"></div>
          <span className="text-sm">{language === 'es' ? 'Negocio Familiar Local' : 'Local Family Business'}</span>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
